/**
 * 不動産情報ライブラリ 全32API一括取得
 * サーバー側で並列処理を行い、結果をまとめて返す
 */

const REINFOLIB_BATCH_URL = 'https://kenzkenz.net/myphp/reinfolib-batch.php'

/**
 * 全32APIを一括取得
 * @param {number} lng - 経度
 * @param {number} lat - 緯度
 * @param {number} zoom - ズームレベル（デフォルト: 14）
 * @returns {Promise<Object>} - APIの結果
 */
export async function fetchReinfolibAll(lng, lat, zoom = 14) {
  try {
    const url = `${REINFOLIB_BATCH_URL}?lat=${lat}&lng=${lng}&z=${zoom}`
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('[reinfolib] 一括取得エラー:', error)
    return {
      success: false,
      error: error.message,
      results: [],
      summary: {
        polygon_hits: [],
        nearest_points: [],
        hazards: [],
        facilities: [],
        prices: [],
        urban_planning: []
      }
    }
  }
}

/**
 * プロパティから意味のある値を抽出
 */
function extractMeaningfulValue(props, keys) {
  for (const key of keys) {
    if (props[key] !== undefined && props[key] !== null && props[key] !== '') {
      return props[key]
    }
  }
  return null
}

/**
 * 国土数値情報コードから日本語名を取得するマッピング
 * 国土数値情報のコード体系に基づく
 */
const PROPERTY_MAPPINGS = {
  // 用途地域 (A29)
  A29_005: '用途地域名',
  A29_006: '建蔽率',
  A29_007: '容積率',

  // 学校 (A27)
  A27_003: '学校名',
  A27_004: '所在地',

  // 土砂災害 (A33)
  A33_001: '現象種類',  // 急傾斜地の崩壊、土石流、地すべり等
  A33_002: '区域区分',  // 警戒区域/特別警戒区域
  A33_005: '区域名',

  // 防火地域 (A32)
  A32_002: '防火区分',

  // 医療施設 (P04)
  P04_002: '施設名',
  P04_003: '診療科目',

  // 福祉施設 (P14)
  P14_003: '施設名',
  P14_004: '施設種別',

  // 学校 (P29)
  P29_003: '学校種別',
  P29_004: '学校名',
}

/**
 * 土砂災害区域区分コードを日本語に変換
 */
function convertSedimentDisasterZone(code) {
  const zones = {
    '1': '土砂災害警戒区域（イエローゾーン）',
    '2': '土砂災害特別警戒区域（レッドゾーン）',
    '警戒区域': '土砂災害警戒区域（イエローゾーン）',
    '特別警戒区域': '土砂災害特別警戒区域（レッドゾーン）'
  }
  return zones[code] || code
}

/**
 * 土砂災害現象種類コードを日本語に変換
 */
function convertSedimentDisasterType(code) {
  const types = {
    '1': '急傾斜地の崩壊',
    '2': '土石流',
    '3': '地すべり'
  }
  return types[code] || code
}

/**
 * ポイント施設の座標を取得（oh3://リンク用）
 * 複数のレスポンス形式に対応:
 *   GeoJSON形式: { geometry: { type: "Point", coordinates: [lng, lat] } }
 *   PHP API形式: { coordinates: [lng, lat] }
 *   フラット形式: { lat: 31.9, lng: 131.4 } or { lat: 31.9, lon: 131.4 }
 *   プロパティ内: { properties: { lat: 31.9, lng: 131.4 } }
 */
function getPointCoords(result) {
  // GeoJSON geometry（型チェックを緩和: Point以外でもcoordinatesがあれば最初の座標を使う）
  if (result.geometry && result.geometry.coordinates) {
    const geom = result.geometry
    if (geom.type === 'Point') return geom.coordinates
    // MultiPoint, LineString等: 最初の座標ペアを使用
    if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
      const first = geom.coordinates[0]
      if (typeof first === 'number') return geom.coordinates // [lng, lat] 直接
      if (Array.isArray(first) && first.length >= 2) return first // [[lng, lat], ...] の最初
    }
  }
  // 直接の coordinates 配列
  if (result.coordinates && Array.isArray(result.coordinates) && result.coordinates.length >= 2) {
    const first = result.coordinates[0]
    if (typeof first === 'number') return result.coordinates
    if (Array.isArray(first) && first.length >= 2) return first
  }
  // フラット形式: lat/lng or lat/lon（reinfolib batch API等）
  if (result.lat != null && result.lng != null) return [Number(result.lng), Number(result.lat)]
  if (result.lat != null && result.lon != null) return [Number(result.lon), Number(result.lat)]
  if (result.latitude != null && result.longitude != null) return [Number(result.longitude), Number(result.latitude)]
  // x/y 形式（reinfolib等で使われる場合がある）
  if (result.x != null && result.y != null) {
    const x = Number(result.x), y = Number(result.y)
    // 日本の座標範囲内チェック (lng: 120-150, lat: 20-50)
    if (x > 100 && y > 20 && y < 50) return [x, y]
  }
  // properties 内の座標
  const props = result.properties
  if (props) {
    if (props.lat != null && props.lng != null) return [Number(props.lng), Number(props.lat)]
    if (props.lat != null && props.lon != null) return [Number(props.lon), Number(props.lat)]
    if (props.latitude != null && props.longitude != null) return [Number(props.longitude), Number(props.latitude)]
    if (props.x != null && props.y != null) {
      const x = Number(props.x), y = Number(props.y)
      if (x > 100 && y > 20 && y < 50) return [x, y]
    }
  }
  // features配列の最初の要素（result自体がコンテナの場合）
  if (result.features && result.features.length > 0) {
    const f = result.features[0]
    const geom = f.geometry
    if (geom && geom.coordinates) {
      if (geom.type === 'Point') return geom.coordinates
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        const first = geom.coordinates[0]
        if (typeof first === 'number') return geom.coordinates
        if (Array.isArray(first) && first.length >= 2) return first
      }
    }
    if (f.coordinates && Array.isArray(f.coordinates)) return f.coordinates
    if (f.lat != null && f.lng != null) return [Number(f.lng), Number(f.lat)]
    if (f.lat != null && f.lon != null) return [Number(f.lon), Number(f.lat)]
  }
  return null
}

/**
 * 施設名にoh3://座標リンクを付与（座標がある場合のみ）
 * GQLチャットで施設名をクリック → 地図フライト用
 */
function linkifyName(facilityName, result) {
  const coords = getPointCoords(result)
  if (coords) {
    return `[${facilityName}](oh3://${coords[0].toFixed(5)},${coords[1].toFixed(5)})`
  }
  return facilityName
}

/**
 * API別の施設名プロパティ抽出（共通ヘルパー）
 */
function extractFacilityName(api, props) {
  switch (api) {
    case 'XKT006': return extractMeaningfulValue(props, ['A27_004_ja', 'P29_004_ja', 'name'])
    case 'XKT007': return extractMeaningfulValue(props, ['preSchoolName_ja', 'name'])
    case 'XKT010': return extractMeaningfulValue(props, ['P04_002_ja', 'P04_001_name_ja', 'name'])
    case 'XKT011': return extractMeaningfulValue(props, ['P14_008_ja', 'welfareFacilityName_ja', 'P14_004_ja', 'name'])
    case 'XKT015': return extractMeaningfulValue(props, ['S12_001_ja', 'S12_001', 'station_name', '駅名', 'name'])
    case 'XKT017': return extractMeaningfulValue(props, ['P27_005_ja', 'P27_003_ja', 'P27_002_ja', 'name'])
    case 'XKT018': return extractMeaningfulValue(props, ['P05_003_ja', 'P05_002_name_ja', 'name'])
    case 'XGT001': return extractMeaningfulValue(props, ['facility_name_ja', 'P20_003_ja', 'P20_002_ja', 'name'])
    default: return null
  }
}

/**
 * 単一のAPIの結果をAI用テキストにフォーマット
 * @param {Object} result - 単一のAPIの結果
 * @returns {string} - フォーマットされたテキスト（「- 」プレフィックス付き）
 */
export function formatSingleResultForAi(result) {
  const api = result.api
  const name = result.name

  // 施設系ポイント: features配列で複数返ってくる場合（XKT010含む全施設API）
  const FACILITY_APIS = ['XKT006', 'XKT007', 'XKT010', 'XKT011', 'XKT015', 'XKT017', 'XKT018', 'XGT001']
  if (FACILITY_APIS.includes(api) && result.features && result.features.length > 0) {
    const items = result.features.slice(0, 5).map(f => {
      const props = f.properties || {}
      const facilityName = extractFacilityName(api, props)
      if (facilityName) {
        return `${linkifyName(facilityName, f)}（${f.distance}m）`
      }
      return `該当あり（${f.distance}m）`
    })
    return `${name}（${result.features.length}件）: ${items.join(', ')}`
  }

  const props = result.properties || {}

  // ポイントデータ（距離あり）
  if (result.distance !== undefined) {
    // 施設名を抽出（API別に異なるプロパティ名に対応）
    let facilityName = null

    // API別の施設名プロパティ（正しいAPIコードに対応）
    switch (api) {
      case 'XKT006': // 学校
        facilityName = extractMeaningfulValue(props, ['A27_004_ja', 'P29_004_ja', 'name'])
        break
      case 'XKT007': // 保育園・幼稚園等
        facilityName = extractMeaningfulValue(props, ['preSchoolName_ja', 'name'])
        break
      case 'XKT010': // 医療機関
        facilityName = extractMeaningfulValue(props, ['P04_002_ja', 'P04_001_name_ja', 'name'])
        break
      case 'XKT011': // 福祉施設
        facilityName = extractMeaningfulValue(props, ['P14_008_ja', 'welfareFacilityName_ja', 'P14_004_ja', 'name'])
        break
      case 'XKT015': // 駅別乗降客数 - S12_001_ja が駅名
        facilityName = extractMeaningfulValue(props, ['S12_001_ja', 'S12_001', 'station_name', '駅名', 'name'])
        break
      case 'XKT017': // 図書館 - P27_005_ja が施設名
        facilityName = extractMeaningfulValue(props, ['P27_005_ja', 'P27_003_ja', 'P27_002_ja', 'name'])
        break
      case 'XKT018': // 市区町村役場及び集会施設等 - P05_003_ja が施設名
        facilityName = extractMeaningfulValue(props, ['P05_003_ja', 'P05_002_name_ja', 'name'])
        break
      case 'XGT001': // 指定緊急避難場所
        facilityName = extractMeaningfulValue(props, ['facility_name_ja', 'P20_003_ja', 'P20_002_ja', 'name'])
        break
      case 'XPT001': { // 不動産取引価格情報
        // u_transaction_price_total_ja: "75,000万円" 形式
        const priceStr = extractMeaningfulValue(props, ['u_transaction_price_total_ja'])
        const landType = extractMeaningfulValue(props, ['land_type_name_ja'])
        const district = extractMeaningfulValue(props, ['district_name_ja'])
        const period = extractMeaningfulValue(props, ['point_in_time_name_ja'])
        let info = ''
        if (landType) info += landType
        if (priceStr) info += (info ? ' ' : '') + priceStr
        if (district) info += (info ? ' ' : '') + district
        if (period) info = `[${period}] ` + info
        return `不動産取引価格: ${info || '情報あり'}（${result.distance}m）`
      }
      case 'XPT002': // 旧フォーマット互換
      case 'XPT002_KOJI': // 地価公示
      case 'XPT002_CHOSA': { // 都道府県地価調査
        // u_current_years_price_ja: "5,750,000(円/㎡)" 形式
        // land_price_type: 0=地価公示, 1=地価調査
        const priceStr = extractMeaningfulValue(props, ['u_current_years_price_ja', 'L01_006', 'L02_006'])
        const priceTypeCode = props.land_price_type
        // APIコードまたはland_price_typeで判定
        let priceType = '地価公示'
        if (api === 'XPT002_CHOSA' || priceTypeCode === 1) {
          priceType = '都道府県地価調査'
        }
        const location = extractMeaningfulValue(props, ['standard_lot_number_ja', 'location_number_ja'])
        // 年度情報（例: "令和7年1月1日"）
        const targetYear = extractMeaningfulValue(props, ['target_year_name_ja'])
        const yearStr = targetYear ? `[${targetYear}] ` : ''
        if (priceStr) {
          // "5,750,000(円/㎡)" から数値を抽出
          const match = priceStr.match(/^([0-9,]+)/)
          if (match) {
            const priceNum = parseInt(match[1].replace(/,/g, ''), 10)
            return `${priceType}: ${yearStr}${priceNum.toLocaleString()}円/㎡${location ? '(' + location + ')' : ''}（${result.distance}m）`
          }
          return `${priceType}: ${yearStr}${priceStr}（${result.distance}m）`
        }
        return `${priceType}: ${yearStr}情報あり（${result.distance}m）`
      }
      default:
        facilityName = extractMeaningfulValue(props, ['name', '施設名', '名称'])
    }

    if (facilityName) {
      return `${name}: ${linkifyName(facilityName, result)}（${result.distance}m）`
    } else {
      return `${name}: 最寄り${result.distance}m`
    }
  }

  // ポリゴンデータ（API別に処理）- 正しいAPIコードに対応
  switch (api) {
    case 'XKT001': {
      // 都市計画区域/区域区分
      const kubun = extractMeaningfulValue(props, [
        'area_classification_ja', 'kubun_name', '区域区分名'
      ])
      return `都市計画区域: ${kubun || '該当あり'}`
    }
    case 'XKT002': {
      // 用途地域
      const yoto = extractMeaningfulValue(props, [
        'use_area_ja', 'A29_005', 'youto_name', '用途地域名'
      ])
      const kenpei = extractMeaningfulValue(props, [
        'u_building_coverage_ratio_ja', 'A29_006', 'kenpei', '建蔽率'
      ])
      const youseki = extractMeaningfulValue(props, [
        'u_floor_area_ratio_ja', 'A29_007', 'youseki', '容積率'
      ])
      let text = `用途地域: ${yoto || '該当あり'}`
      if (kenpei) text += `（建蔽率${kenpei}%）`
      if (youseki) text += `（容積率${youseki}%）`
      return text
    }
    case 'XKT003': {
      // 立地適正化計画
      const areaType = extractMeaningfulValue(props, [
        'kubun_name_ja', 'area_type_name', '区域種別'
      ])
      return `立地適正化計画: ${areaType || '該当区域内'}`
    }
    case 'XKT004': {
      // 小学校区
      const schoolName = extractMeaningfulValue(props, [
        'A27_004_ja', 'school_name', '学校名'
      ])
      return `小学校区: ${schoolName || '該当あり'}`
    }
    case 'XKT005': {
      // 中学校区 - A32_004_ja が学校名
      const schoolName = extractMeaningfulValue(props, [
        'A32_004_ja', 'A27_004_ja', 'school_name', '学校名'
      ])
      return `中学校区: ${schoolName || '該当あり'}`
    }
    case 'XKT013': {
      // 将来推計人口250mメッシュ（基準年は2025年）
      const pop2025Raw = extractMeaningfulValue(props, ['pop_2025', 'PTN_2025', '2025年人口'])
      const pop2050Raw = extractMeaningfulValue(props, ['pop_2050', 'PTN_2050', '2050年人口'])
      const pop2025 = pop2025Raw ? Math.round(Number(pop2025Raw)) : null
      const pop2050 = pop2050Raw ? Math.round(Number(pop2050Raw)) : null
      let text = '将来推計人口: '
      if (pop2025 && pop2050) {
        text += `2025年${pop2025.toLocaleString()}人→2050年${pop2050.toLocaleString()}人`
      } else if (pop2025) {
        text += `2025年${pop2025.toLocaleString()}人`
      } else {
        text += '該当メッシュ内'
      }
      return text
    }
    case 'XKT014': {
      // 防火・準防火地域
      const bouka = extractMeaningfulValue(props, [
        'fire_prevention_ja', 'A32_002', 'bouka_name', '防火地域名'
      ])
      return `防火地域: ${bouka || '該当あり'}`
    }
    case 'XKT016': {
      // 災害危険区域
      return '災害危険区域: 該当'
    }
    case 'XKT019': {
      // 自然公園地域
      const parkName = extractMeaningfulValue(props, ['park_name', '公園名', 'name'])
      return `自然公園地域: ${parkName || '該当'}`
    }
    case 'XKT020': {
      // 大規模盛土造成地マップ - embankment_classification が種別
      const moriType = extractMeaningfulValue(props, [
        'embankment_classification', 'large_embankment_type_ja', 'type_name_ja', 'kubun_name', '盛土種別'
      ])
      return `大規模盛土造成地: ${moriType || '該当区域内'}`
    }
    case 'XKT021': {
      // 地すべり防止地区
      return '地すべり防止地区: 該当'
    }
    case 'XKT022': {
      // 急傾斜地崩壊危険区域
      return '急傾斜地崩壊危険区域: 該当'
    }
    case 'XKT023': {
      // 地区計画
      const planName = extractMeaningfulValue(props, ['plan_name', '地区計画名', 'name'])
      return `地区計画: ${planName || '該当'}`
    }
    case 'XKT024': {
      // 高度利用地区 - advanced_type_ja が種別
      const koType = extractMeaningfulValue(props, [
        'advanced_type_ja', 'advanced_name', 'high_use_district_type_ja', 'type_name_ja', 'kubun_name', '種別'
      ])
      return `高度利用地区: ${koType || '該当'}`
    }
    case 'XKT025': {
      // 液状化発生傾向図
      const topo = extractMeaningfulValue(props, [
        'topographic_classification_name_ja', '地形分類'
      ])
      const level = extractMeaningfulValue(props, [
        'liquefaction_tendency_level', 'level', 'rank'
      ])
      const note = extractMeaningfulValue(props, ['note', '備考'])

      let text = '液状化傾向: '
      if (topo) text += topo
      if (note) text += topo ? `（${note}）` : note
      else if (level) text += topo ? `（レベル${level}）` : `レベル${level}`
      if (!topo && !note && !level) text += '該当あり'
      return text
    }
    case 'XKT026': {
      // 洪水浸水想定区域 - A31a_205 が浸水深ランク
      const depthRankMap = {
        '1': '0.5m未満',
        '2': '0.5m～3.0m',
        '3': '3.0m～5.0m',
        '4': '5.0m～10.0m',
        '5': '10.0m～20.0m',
        '6': '20.0m以上',
      }
      const depthRank = props.A31a_205 ? depthRankMap[String(props.A31a_205)] : null
      const depth = depthRank || extractMeaningfulValue(props, [
        'flood_depth_ja', 'flood_depth_name_ja', 'depth_name_ja',
        'depth_name', '浸水深', 'flood_depth',
        'A31_101', 'rank_name', '想定浸水深', 'rank'
      ])
      return `洪水浸水想定: ${depth || '該当区域内'}`
    }
    case 'XKT027': {
      // 高潮浸水想定区域 - A49_003 が浸水深
      const depth = extractMeaningfulValue(props, [
        'A49_003', 'storm_surge_depth_ja', 'depth_name_ja',
        'depth_name', '浸水深', 'storm_surge_depth', 'rank'
      ])
      return `高潮浸水想定: ${depth || '該当区域内'}`
    }
    case 'XKT028': {
      // 津波浸水想定 - A40_003 が浸水深
      const depth = extractMeaningfulValue(props, [
        'A40_003', 'tsunami_depth_ja', 'depth_name_ja',
        'depth_name', '浸水深', 'tsunami_depth', 'rank'
      ])
      return `津波浸水想定: ${depth || '該当区域内'}`
    }
    case 'XKT029': {
      // 土砂災害警戒区域（A33データ）
      let zoneCode = extractMeaningfulValue(props, ['A33_002', 'zone_type', '区域区分'])
      let typeCode = extractMeaningfulValue(props, ['A33_001', 'phenomenon_type', '現象種類'])

      let zone = zoneCode ? convertSedimentDisasterZone(zoneCode) : null
      let type = typeCode ? convertSedimentDisasterType(typeCode) : null

      let text = '土砂災害: '
      if (zone) text += zone
      if (type) text += zone ? `（${type}）` : type
      if (!zone && !type) text += '該当区域内'
      return text
    }
    case 'XKT030': {
      // 都市計画道路
      const roadName = extractMeaningfulValue(props, ['road_name', '路線名', 'name'])
      return `都市計画道路: ${roadName || '該当'}`
    }
    case 'XKT031': {
      // 人口集中地区
      const population = extractMeaningfulValue(props, ['A16_007', 'population', '人口'])
      const area = extractMeaningfulValue(props, ['A16_008', 'area', '面積']) // A16_008はすでにkm²単位
      let text = '人口集中地区: 該当'
      if (population) text += `（人口${Number(population).toLocaleString()}人）`
      if (area) text += `（面積${Number(area).toFixed(2)}km²）`
      return text
    }
    case 'XST001': {
      // 災害履歴
      const type = extractMeaningfulValue(props, [
        'disaster_type_ja', 'disaster_type_name_ja', 'type_name_ja',
        'disaster_type', '災害種別', 'type_name',
        'category', '災害名'
      ])
      const date = extractMeaningfulValue(props, [
        'disaster_date', '発生日', 'date', 'year'
      ])
      let text = '災害履歴: '
      if (type) text += type
      if (date) text += `（${date}）`
      if (!type && !date) text += 'あり'
      return text
    }
    default:
      // その他のAPI（名前だけ出力）
      return `${name}: 該当あり`
  }
}

/**
 * reinfolibデータをAI用のテキストに整形
 * @param {Object} data - fetchReinfolibAllの結果
 * @returns {string} - AI用のテキスト
 */
export function formatReinfolibForAi(data) {
  if (!data || !data.success || !data.results || data.results.length === 0) {
    return ''
  }

  const lines = []
  lines.push('【不動産情報ライブラリ データ】')

  // 各APIの結果を処理（formatSingleResultForAiを使用）
  for (const result of data.results) {
    lines.push('- ' + formatSingleResultForAi(result))
  }

  // 結果がない場合
  if (lines.length === 1) {
    return ''
  }

  const resultText = lines.join('\n')

  // デバッグログ: AIに送るテキストを表示
  console.group('[reinfolib] 不動産情報ライブラリ - AIに送るテキスト')
  console.groupEnd()

  return resultText
}

/**
 * reinfolibデータを表示用に整形
 * @param {Object} data - fetchReinfolibAllの結果
 * @returns {Object} - カテゴリ別に整形されたデータ
 */
export function formatReinfolibForDisplay(data) {
  if (!data || !data.success) {
    return null
  }

  return {
    urbanPlanning: data.summary.urban_planning || [],
    hazards: data.summary.hazards || [],
    facilities: data.summary.facilities || [],
    prices: data.summary.prices || [],
    hitCount: data.hit_count || 0,
    apiCount: data.api_count || 32
  }
}

/**
 * XKT013（将来推計人口250mメッシュ）から詳細データを抽出
 * @param {Object} data - fetchReinfolibAllの結果
 * @returns {Object|null} - 将来推計人口データ
 */
export function extractFuturePopulation(data) {
  if (!data || !data.success || !data.results) {
    return null
  }

  const xkt013 = data.results.find(r => r.api === 'XKT013')

  // データ構造パターン1: features配列がある場合
  // データ構造パターン2: propertiesが直接ある場合
  let props = {}
  if (xkt013?.features?.length > 0) {
    props = xkt013.features[0].properties || {}
  } else if (xkt013?.properties) {
    props = xkt013.properties
  } else {
    return null
  }

  // 人口推計データの年度キーを探索（PTN_YYYY形式）
  const years = [2020, 2025, 2030, 2035, 2040, 2045, 2050, 2055, 2060, 2065, 2070]
  const populationByYear = {}

  for (const year of years) {
    // 複数の形式を試す
    const keys = [`PTN_${year}`, `pop_${year}`, `${year}年人口`]
    for (const key of keys) {
      if (props[key] !== undefined && props[key] !== null && props[key] !== '') {
        const value = Math.round(Number(props[key]))
        if (!isNaN(value)) {
          populationByYear[year] = value
          break
        }
      }
    }
  }

  // データがなければnullを返す
  if (Object.keys(populationByYear).length === 0) {
    return null
  }

  // 年齢区分別人口を取得
  // PTA: 年少人口（0-14歳）, PTB: 生産年齢人口（15-64歳）, PTC: 前期高齢（65-74歳）, PTD: 後期高齢（75歳以上）
  const ageBreakdown = {}
  for (const year of years) {
    const ptaKey = `PTA_${year}`
    const ptbKey = `PTB_${year}`
    const ptcKey = `PTC_${year}`
    const ptdKey = `PTD_${year}`

    // 空文字やnull/undefinedを適切に処理
    const parseValue = (val) => {
      if (val === undefined || val === null || val === '' || val === 0) return null
      const num = Math.round(Number(val))
      return isNaN(num) || num === 0 ? null : num
    }

    const pta = parseValue(props[ptaKey])
    const ptb = parseValue(props[ptbKey])
    const ptc = parseValue(props[ptcKey])
    const ptd = parseValue(props[ptdKey])

    // 老年人口（65歳以上）= PTC + PTD
    const elderly = (ptc !== null && ptd !== null) ? ptc + ptd :
                    (ptc !== null ? ptc : (ptd !== null ? ptd : null))

    // 有効なデータがある場合のみ追加
    const totalPop = populationByYear[year] || 0
    const rawYoung = pta || 0
    const rawWorking = ptb || 0
    const rawElderly = elderly || 0
    const ageSum = rawYoung + rawWorking + rawElderly

    // 年齢構成の合計が総人口の50%以上なら有効と判断
    if (totalPop > 0 && ageSum >= totalPop * 0.5 && ageSum > 0) {
      // 総人口に合わせて正規化（年齢内訳の合計 ≠ 総人口のズレを補正）
      const ratio = totalPop / ageSum
      const normYoung = Math.round(rawYoung * ratio)
      const normWorking = Math.round(rawWorking * ratio)
      // 端数は高齢者に寄せて合計を厳密に一致させる
      const normElderly = totalPop - normYoung - normWorking

      ageBreakdown[year] = {
        young: normYoung,       // 年少人口（0-14歳）
        working: normWorking,   // 生産年齢人口（15-64歳）
        elderly: normElderly,   // 老年人口（65歳以上）
        earlyElderly: ptc ? Math.round(ptc * ratio) : 0,
        lateElderly: ptd ? Math.round(ptd * ratio) : 0
      }
    }
  }

  // 基準年（2025年）からの変化率を計算
  const baseYear = 2025
  const basePopulation = populationByYear[baseYear]
  const changeRates = {}

  if (basePopulation && basePopulation > 0) {
    for (const [year, pop] of Object.entries(populationByYear)) {
      if (Number(year) !== baseYear) {
        changeRates[year] = Math.round(((pop - basePopulation) / basePopulation) * 1000) / 10
      }
    }
  }

  return {
    meshSize: '250m',
    populationByYear,
    ageBreakdown,
    changeRates,
    baseYear,
    pop2025: populationByYear[2025] || null,
    pop2030: populationByYear[2030] || null,
    pop2050: populationByYear[2050] || null,
    changeRate2030: changeRates[2030] || null,
    changeRate2050: changeRates[2050] || null
  }
}
